import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

const GalleryPage = ({ title, images }) => {
  const carouselRef = useRef(null);
  const snapTimeout = useRef(null);

  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    // Physics state
    let currentScroll = container.scrollLeft;
    let targetScroll = container.scrollLeft;
    let isAnimating = false;

    const lerp = (start, end, factor) => {
      // Precise lerp for smooth stopping
      const val = start + (end - start) * factor;
      return Math.abs(end - val) < 0.1 ? end : val;
    };

    const updateScroll = () => {
      if (!container) return;
      
      // Lerp towards target
      currentScroll = lerp(currentScroll, targetScroll, 0.08); // 0.08 = smoothness factor
      container.scrollLeft = currentScroll;

      // Continue animation if we're not close enough
      if (Math.abs(targetScroll - currentScroll) > 0.5) {
        requestAnimationFrame(updateScroll);
        isAnimating = true;
      } else {
        isAnimating = false;
      }
    };

    const handleWheel = (e) => {
      // Prevent default vertical scrolling to allow horizontal takeover
      e.preventDefault();

      // Clear any pending snap
      if (snapTimeout.current) clearTimeout(snapTimeout.current);

      // Update target based on wheel
      // Multiplier affects speed
      targetScroll += e.deltaY * 2.5;

      // Clamp target to bounds roughly first
      const maxScroll = container.scrollWidth - container.clientWidth;
      targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

      // Start animation loop if not running
      if (!isAnimating) {
        requestAnimationFrame(updateScroll);
      }

      // Set timeout for snapping
      snapTimeout.current = setTimeout(() => {
        // Find the nearest snap point based on variable width children
        const containerCenter = container.clientWidth / 2;
        const currentTargetCenter = targetScroll + containerCenter;

        let bestSnap = targetScroll;
        let minDistance = Infinity;

        // Iterate over image cards (assumes they are the div children)
        // We filter to ensure we act on the item cards
        const items = Array.from(container.children).filter(child => 
            child.tagName === 'DIV' && !child.className.includes('exclude-snap')
        );

        items.forEach(item => {
            // Calculate where this item is relative to the *scrollable content* start.
            // offsetLeft is relative to the parent provided parent is positioned. 
            // container is flex-row, so offsetLeft works.
            const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
            
            // We want itemCenter to align with containerCenter
            // So logical scroll position = itemCenter - containerCenter
            const idealScroll = itemCenter - containerCenter;
            
            const distance = Math.abs(idealScroll - targetScroll);
            
            if (distance < minDistance) {
                minDistance = distance;
                bestSnap = idealScroll;
            }
        });

        // Final Clamp
        bestSnap = Math.max(0, Math.min(bestSnap, maxScroll));
        
        targetScroll = bestSnap;
        
        // Ensure animation continues to the new snap point
        if (!isAnimating) {
          requestAnimationFrame(updateScroll);
        }
      }, 150); // Wait 150ms after last scroll event
    };

    // Add passive: false to allow preventDefault
    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (snapTimeout.current) clearTimeout(snapTimeout.current);
    };
  }, [images]); // Re-bind if images change

  return (
    <div style={{
      height: '100vh',
      overflow: 'hidden',
      background: 'var(--bg-color)',
      color: 'var(--text-color)',
      padding: '2rem',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', flexShrink: 0 }}>
        <Link to="/" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem', 
          fontSize: '1.2rem',
          textTransform: 'uppercase', 
          letterSpacing: '1px',
          opacity: 0.7,
          transition: 'opacity 0.3s'
        }}>
          <FaArrowLeft /> Back
        </Link>
        <h1 style={{ 
          flex: 1, 
          textAlign: 'center', 
          fontSize: '3rem', 
          margin: 0,
          fontFamily: "'Playfair Display', serif"
        }}>
          {title}
        </h1>
        <div style={{ width: '80px' }}></div> {/* Spacer for alignment */}
      </div>

      {/* Carousel Container */}
      <div 
        ref={carouselRef}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: '4rem', // Increased gap for better separation of variable widths
          padding: '0 50vw 0 50vw', // Large padding to allow first/last items to center
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} 
        className="gallery-carousel"
      >
        <style>{`
          .gallery-carousel::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        
        {images.map((img, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: index * 0.05 }}
            viewport={{ once: true }}
            style={{
              // Variable width setup
              width: 'auto', 
              height: '70vh', // Fixed height
              flex: '0 0 auto', // Do not shrink
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
              display: 'flex', // Ensure img behaves well
              justifyContent: 'center'
            }}
          >
            <img 
              src={img} 
              alt={`${title} ${index + 1}`} 
              draggable={false}
              style={{
                width: 'auto',
                height: '100%',
                objectFit: 'contain', // Ensure full image is visible
                display: 'block'
              }}
            />
          </motion.div>
        ))}
        
        {/* Helper to ensure padding works correctly in some flex implementations */}
        <div className="exclude-snap" style={{ width: '1px', flex: '0 0 1px' }}></div>
      </div>
      
      <div style={{ textAlign: 'center', opacity: 0.4, marginTop: '1rem', fontSize: '0.9rem' }}>
        Scroll or Drag
      </div>
    </div>
  );
};

export default GalleryPage;
